import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { IGlobalState } from '../model/IGlobalState';
import { FaSpinner, FaEye, FaEyeSlash } from 'react-icons/fa';
import api from '../api/api';
import { SetUser } from '../actions/SetUser';

const Wrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;

    .box {
        width: 460px;
        display: flex;
        flex-direction: column;
    }
    .box > * {
        margin-top: 10px;
    }
    .logo-container {
        display: flex;
        justify-content: center;
        padding: 2rem;
    }
    h2 {
        margin: 0;
    }
    p, li {
        line-height: 1.5;
    }
    .tip {
        background: #f3f6fa;
        border-left: 4px solid #1f4e79;
        padding: 0.75rem 1rem;
        font-size: 0.95rem;
    }
    /* visible + normal size: Chrome ignores hidden/off-screen fields when pairing username with the saved password */
    .username-readonly {
        background: #f3f6fa;
        color: #6b7480;
    }
    .password-row {
        display: flex;
    }
    .password-row input {
        flex: 1;
    }
    .password-row button {
        margin-left: 6px;
        padding: 0 10px;
    }
    .error {
        font-weight: bold;
    }
`;

const ResetPasswordPage = () => {
    // uncontrolled: Chrome's "use strong password" fill gets wiped by a controlled re-render
    const passwordRef = useRef<HTMLInputElement>(null);
    const [ show, setShow ] = useState(false);
    const [ error, setError ] = useState('');
    const [ loading, setLoading ] = useState(false);
    const dispatch = useDispatch();
    const username = useSelector((state: IGlobalState) => state.user.username);

    const save = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const result = await api().changePassword(passwordRef.current?.value || '');
            dispatch(SetUser(result.user));
            window.location.href = '/';
        } catch (err: any) {
            setError(err?.response?.data || 'Could not change password, please try again');
            setLoading(false);
        }
    };

    return <Wrapper>
        <form className="box" onSubmit={save}>
            <div className="logo-container investi-dark-blue"><img src="/images/logo.svg" alt="logo" className="front-page-logo" /></div>
            <h2>Choose a new password</h2>
            <p>You are required to enter a new password before continuing. It must be at least 8 characters with at least one letter and one number, and you will use it the next time you log in.</p>
            <div className="tip">
                <strong>Tip:</strong> most browsers (Chrome, Safari, Edge, Firefox) can generate a strong password for you and save it to
                your password manager. Click into the field below and look for the <em>"Suggest strong password"</em> option — you will
                not need to remember it, your browser will fill it in for you next time.
            </div>
            <input type="text" autoComplete="username" value={username} readOnly tabIndex={-1} className="username-readonly" />
            <div className="password-row">
                <input ref={passwordRef} type={show ? 'text' : 'password'} autoComplete="new-password" placeholder="New password" autoFocus
                    disabled={loading} />
                <button type="button" onClick={() => setShow(!show)} title={show ? 'Hide password' : 'Show password'}>
                    {show ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                </button>
            </div>
            {error && <p className="error">{error}</p>}
            <button type="submit" disabled={loading}>Save new password &nbsp;
                {loading && <FaSpinner className="fa-spin" size={16} color="white" />}
            </button>
        </form>
    </Wrapper>;
};

export default ResetPasswordPage;
